import { useLogin } from '@refinedev/core';
import { m } from 'framer-motion';
import { AtSign, Lock, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { LoginParams } from '../auth/authProvider';
import { useBranding } from '../branding/BrandingProvider';
import { FloatingPaths } from '../components/FloatingPaths';
import { Alert, Button, Field } from '../components/ui';

/**
 * Pantalla de acceso (RF-08, Módulo 9).
 *
 * Dos pasos, porque el contrato de login tiene dos finales posibles: sesión
 * emitida o desafío de MFA. El segundo paso no vuelve a pedir la contraseña —
 * el backend ya emitió un token de un solo uso para canjear el TOTP.
 */
export function LoginPage() {
  const { mutate: login, isLoading } = useLogin<LoginParams>();
  const { branding } = useBranding();

  const [step, setStep] = useState<'password' | 'mfa'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const params: LoginParams =
      step === 'password' ? { kind: 'password', email, password } : { kind: 'mfa', totp };

    login(params, {
      onSuccess: (result) => {
        if (result.success) return;
        if ((result as { mfaRequired?: boolean }).mfaRequired) {
          setStep('mfa');
          setTotp('');
          setError(null);
          return;
        }
        setError(result.error?.message ?? 'No se pudo iniciar sesión');
      },
      onError: (cause) => setError(cause.message),
    });
  };

  const productName = branding.productName || 'Omnia Control Center';

  return (
    <main className="relative min-h-screen lg:grid lg:grid-cols-2">
      {/* Panel de marca. Oculto en mobile: en una pantalla angosta es puro
          adorno y le quita alto al formulario, que es lo que importa ahí. */}
      <div className="bg-muted/60 relative hidden flex-col justify-between overflow-hidden p-12 lg:flex">
        <FloatingPaths position={1} />
        <FloatingPaths position={-1} />

        <div className="relative z-10 flex items-center gap-2">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt="" className="h-7 w-auto max-w-[10rem]" />
          ) : (
            <span className="text-xl font-semibold tracking-tight">{productName}</span>
          )}
        </div>

        <div className="relative z-10 max-w-lg space-y-3">
          <p className="text-2xl leading-snug">
            Soporte remoto sin exponer un puerto: terminal, archivos, pantalla y scripts, todo por
            un solo túnel saliente desde el nodo.
          </p>
          <p className="text-muted-foreground font-mono text-base">
            Cliente → Sucursal → Nodo, con auditoría de cada acción.
          </p>
        </div>
      </div>

      <div className="relative flex min-h-screen flex-col items-center justify-center p-6 lg:p-16">
        <m.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="grid w-full max-w-md gap-8"
        >
          <div className="lg:hidden">
            <h1 className="text-lg font-semibold tracking-tight">{productName}</h1>
          </div>

          <div>
            <h2 className="text-3xl font-semibold tracking-tight">
              {step === 'password' ? 'Entrar' : 'Verifica tu identidad'}
            </h2>
            <p className="text-muted-foreground mt-1 text-base">
              {step === 'password'
                ? 'Consola de soporte'
                : 'Seis dígitos de tu aplicación de autenticación'}
            </p>
          </div>

          <div className="rounded-xl bg-card p-8 shadow-lg">
            <form onSubmit={submit} className="grid gap-4">
              {step === 'password' ? (
                <>
                  <Field
                    label="Correo"
                    type="email"
                    autoComplete="username"
                    required
                    icon={<AtSign className="size-4" />}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <Field
                    label="Contraseña"
                    type="password"
                    autoComplete="current-password"
                    required
                    icon={<Lock className="size-4" />}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </>
              ) : (
                <Field
                  label="Código de verificación"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  required
                  autoFocus
                  icon={<ShieldCheck className="size-4" />}
                  value={totp}
                  hint="Seis dígitos de tu aplicación de autenticación."
                  onChange={(event) => setTotp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              )}

              {error && <Alert>{error}</Alert>}

              <Button type="submit" variant="primary" size="lg" disabled={isLoading}>
                {isLoading ? 'Comprobando…' : step === 'password' ? 'Entrar' : 'Verificar'}
              </Button>

              {step === 'mfa' && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setStep('password');
                    setError(null);
                  }}
                >
                  Volver
                </Button>
              )}
            </form>

            {/*
              La entrada a la página de códigos, junto al botón de entrar (plan
              de enrolamiento por código, §7).

              Va **aquí y no en un menú**: quien la necesita está delante de la
              caja de un cliente, acaba de teclear el dominio del OCC y lo que le
              sale es esta pantalla. Sin este enlace tendría que saberse la ruta
              `/i` de memoria o pedir sesión en una máquina donde no debe
              abrirla, que son las dos cosas que el plan quita de en medio.

              Solo en el primer paso: a mitad de un desafío de MFA, un enlace que
              se lleva la pantalla tira el token de un solo uso que el backend ya
              emitió.
            */}
            {step === 'password' && (
              <p className="text-muted-foreground mt-4 text-sm">
                ¿Vas a instalar un equipo?{' '}
                <Link to="/i" className="font-medium underline underline-offset-4">
                  Ingresa tu código
                </Link>
              </p>
            )}
          </div>
        </m.div>
      </div>
    </main>
  );
}
